import { DefaultLocales } from "./locales";
import { DefaultTemplates } from "./templates";
import { DateRangeInterface, DateTimeDisplayInterface, KTDatepickerConfigInterface, LocaleConfigInterface, SelectedDateInterface } from "./types";

export const DefaultConfig: KTDatepickerConfigInterface = {
	locale: 'en-US',
	locales: DefaultLocales, // all locales

	weekDays: 'min',
	forceLeadingZero: true,

	// 0-indexed month
	// minDate: new Date(2024, 7, 20),
	// maxDate: new Date(2024, 8, 10),

	// supported formats: refer to dateFormat
	// minDate: '20/08/2024',
	// maxDate: '10/09/2024',

	// Calendar
	visibleMonths: 1, // visible months calendar to show
	visibleYears: 10, // visible years span to show on year selection

	// Date
	dateFormat: 'dd/MM/yyyy',

	// Time
	enableTime: false,
	timeFormat: 'hh:mm:ss A ZZZ', // 12-hours time format
	// timeFormat: 'HH:mm:ss ZZZ', // 24-hours time format
	am: 'AM',
	pm: 'PM',
	hourStep: 1,
	// minuteStep: 5,
	// secondStep: 10,
	// disabledHours: [0, 1, 2, 3, 4, 5, 6, 22, 23],
	// disabledMinutes: [0, 1, 2, 3],

	// Date range
	range: false,
	rangeSeparator: ' - ',

	// Element selectors
	inputSelector: 'data-kt-datepicker-input',
	startInputSelector: 'data-kt-datepicker-start',
	endInputSelector: 'data-kt-datepicker-end',
	segmentSelector: 'data-kt-datepicker-segment',
	monthContainerSelector: 'data-kt-datepicker-month-container',
	monthDisplaySelector: 'data-kt-datepicker-month-display',
	yearDisplaySelector: 'data-kt-datepicker-year-display',
	daysOfWeekSelector: 'data-kt-datepicker-days-of-week',
	calendarGridSelector: 'data-kt-datepicker-grid',
	prevBtnSelector: 'data-kt-datepicker-prev-btn',
	nextBtnSelector: 'data-kt-datepicker-next-btn',
	monthYearDisplaySelector: 'data-kt-datepicker-month-year-display',
	dateCellSelector: 'data-kt-datepicker-cell',
	monthYearSelector: 'data-kt-datepicker-month-year-selector',
	monthOptionSelector: 'data-kt-datepicker-month-option',
	yearOptionSelector: 'data-kt-datepicker-year-option',
	timeInputSelector: 'data-kt-datepicker-time-input',
	monthOptionsSelector: 'data-kt-datepicker-month-options',
	yearOptionsSelector: 'data-kt-datepicker-year-options',
    dropdownSelector: 'data-kt-datepicker-dropdown',
    mainDisplaySelector: 'data-kt-datepicker-display',

	// Events
	initEvent: 'init',
	dateChangeEvent: 'date-change',
	keyboardNavigateEvent: 'keyboard-navigate',
	calendarRenderedEvent: 'rendered',
	calendarPreviousMonthEvent: 'previous-month',
	calendarNextMonthEvent: 'next-month',

	// Classes
	selectedClass: 'selected',
	hoveredInRangeClass: 'hovered-in-range',

	containerClass: 'flex',
	monthContainerClass: 'hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer font-semibold text-sm',
	daysOfWeekClass: 'dow text-center text-sm font-medium text-gray-500 dark:text-gray-400',
	dayCellClass: 'hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-center font-semibold text-sm',
	dayCellSelectedClass: 'selected',
	dayCellCurrentClass: 'current',
	dayCellDisabledClass: 'disabled cursor-not-allowed opacity-50',
	dayCellInRangeClass: 'selected',
	dayCellStartClass: 'start',
	dayCellEndClass: 'end',
	monthYearSelectorContainerClass: 'bg-white shadow-lg rounded-md p-4',
	monthYearDisplayClass: 'flex items-center cursor-pointer',
	monthYearDisplayTextClass: 'text-lg font-bold mx-2',
	monthYearOptionsContainerClass: 'flex',
	monthOptionClass: 'py-2 px-4 text-sm hover:bg-gray-200 focus:outline-none',
	monthOptionSelectedClass: 'selected',
	monthOptionCurrentClass: 'current',
	yearOptionClass: 'py-2 px-4 text-sm hover:bg-gray-200 focus:outline-none',
	yearOptionSelectedClass: 'selected',
	yearOptionCurrentClass: 'current',
	datetimeDisplayClass: 'flex items-center space-x-2',
	datetimeSegmentClass: 'cursor-pointer',
	timeListContainerClass: 'bg-white shadow-md rounded p-3',
	timeSelectorFlexContainerClass: 'flex flex-wrap justify-center',
	timePartContainerClass: 'w-1/2 sm:w-1/4 p-1',
	timePartLabelClass: 'block text-center text-sm font-medium text-gray-700 mb-1',
	timeSelectClass: 'w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm',
};

export class KTDatepickerStateManager {

	private readonly _config: KTDatepickerConfigInterface;
	private _localeConfig: LocaleConfigInterface;

	private _selectedDate: Date | null = null;
	private _selectedDateRange: Required<DateRangeInterface> = { startDate: null, endDate: null };

	constructor(config?: KTDatepickerConfigInterface) {
		this._config = this._initDefaultConfig(config);
		this._setLocale(this._config.locale || 'en-US');
	}

	private _setLocale(locale: string) {
		this._config.locale = locale;
		this._localeConfig = this._getLocalesConfig(locale);
	}

	private _getLocalesConfig(locale: string): LocaleConfigInterface {
		const config = this._config.locales as { [key: string]: LocaleConfigInterface };
		if (config[locale]) {
			return config[locale];
		}
		console.warn(`Locale '${locale}' not found. Falling back to 'en-US'.`);
		return config['en-US'];
	}

	private _initDefaultConfig(config: KTDatepickerConfigInterface): any {
		return {
			...DefaultConfig, ...config,
			templates: { ...DefaultTemplates, ...DefaultConfig.templates, ...(config?.templates || {}) }
		};
	}

	public padZero(num: number, maxLength = 2, paddingChar = '0'): string {
		if (!this._config.forceLeadingZero) {
			return String(num);
		}
		return String(num).padStart(maxLength, paddingChar);
	}

	public getConfig(): KTDatepickerConfigInterface {
		return this._config;
	}

	public getTemplates(): Partial<typeof DefaultTemplates> {
		return this._config.templates;
	}

	public getDateFormat(): string {
		return this._config.dateFormat || 'dd/MM/yyyy';
	}

	public getTimeFormat(): string {
		return this._config.timeFormat || 'hh:mm:ss A ZZZ';
	}

	public getFirstDayOfWeek(): number {
		return this._localeConfig.firstDayOfWeek || 0;
	}

	public getWeekdaysShort(): string[] {
		return this._localeConfig.dayNamesShort || this._localeConfig.dayNames.map(day => day.slice(0, 3));
	}

	public getMonthNames(): string[] {
		return this._localeConfig.monthNames || this._localeConfig.monthNamesShort;
	}

	public getWeekdaysMin(): string[] {
		return this._localeConfig.dayNamesMin || this._localeConfig.dayNames.map(day => day.slice(0, 1));
	}

	public getWeekdays(): string[] {
		return this._config.weekDays === 'min' ? this.getWeekdaysMin() : this.getWeekdaysShort();
	}

	public getDisabledHours(): number[] {
		return this._config.disabledHours || [];
	}

	public getDisabledMinutes(): number[] {
		return this._config.disabledMinutes || [];
	}

	public strToDate(dateString: string, format: string): Date | null {
		const formatParts = format.split(/[^A-Za-z]/);
		const dateParts = dateString.split(/[^0-9\s]/).map(part => part.trim());

		if (formatParts.length !== dateParts.length) {
			return null; // Mismatch between format and date string
		}

		const dateMap: { [key: string]: number } = {
			year: 0,
			month: 0,
			day: 1,
			hours: 0,
			minutes: 0,
			seconds: 0,
		};

		formatParts.forEach((formatPart, index) => {
			const partValue = parseInt(dateParts[index], 10);

			switch (formatPart) {
				case 'YYYY':
				case 'yyyy':
					dateMap.year = partValue;
					break;
				case 'MM':
					dateMap.month = partValue - 1; // Months are 0-based in JavaScript Date
					break;
				case 'DD':
				case 'dd':
					dateMap.day = partValue;
					break;
				case 'HH':
				case 'hh':
					dateMap.hours = partValue;
					break;
				case 'mm':
					dateMap.minutes = partValue;
					break;
				case 'ss':
					dateMap.seconds = partValue;
					break;
				default:
					break;
			}
		});

		return new Date(
			dateMap.year,
			dateMap.month,
			dateMap.day,
			dateMap.hours,
			dateMap.minutes,
			dateMap.seconds
		);
	}

	public getAmPm(hours: number): string {
		return hours < 12 ? this._config.am : this._config.pm;
	}

	public formatTimezone(date: Date): string {
		const timezoneOffset = date.getTimezoneOffset();
		if (timezoneOffset === 0) {
			return 'UTC';
		} else {
			const offsetHours = this.padZero(Math.abs(timezoneOffset / 60));
			const timezoneSign = timezoneOffset >= 0 ? '-' : '+';
			return `GMT${timezoneSign}${offsetHours}`;
		}
	}

	public formatTime(date: Date, format: string = null): string {
		if (!date) {
			return '';
		}

		const timeFormat = format || this.getTimeFormat();
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const seconds = date.getSeconds();
		const amPm = this.getAmPm(hours);
		const hour = hours % 12 || 12;

		const formattedTime = timeFormat
			.replace('hh', this.padZero(hour))
			.replace('HH', this.padZero(hours))
			.replace('mm', this.padZero(minutes))
			.replace('ss', this.padZero(seconds))
			.replace('a', amPm.toLocaleLowerCase(this._config.locale))
			.replace('A', amPm.toLocaleUpperCase(this._config.locale))
			.replace('zzz', this.formatTimezone(date).toLocaleLowerCase(this._config.locale))
			.replace('ZZZ', this.formatTimezone(date).toLocaleUpperCase(this._config.locale));

		return formattedTime;
	}

	public isMinMaxConfigRange(date: Date): boolean {
		const { minDate, maxDate } = this._config;

		if (minDate && date < (minDate instanceof Date ? minDate : this.parseDateString(minDate, this.getDateFormat()))) {
			return true;
		}

		if (maxDate && date > (maxDate instanceof Date ? maxDate : this.parseDateString(maxDate, this.getDateFormat()))) {
			return true;
		}

		return false;
	}

	public parseDateString(dateString: string, dateFormat: string): Date | null {
		const dayIndex = dateFormat.indexOf('dd');
		const monthIndex = dateFormat.indexOf('MM');
		const yearIndex = dateFormat.indexOf('yyyy');

		const day = parseInt(dateString.slice(dayIndex, dayIndex + 2), 10);
		const month = parseInt(dateString.slice(monthIndex, monthIndex + 2), 10) - 1; // Months are 0-based
		const year = parseInt(dateString.slice(yearIndex, yearIndex + 4), 10);

		// Validate parsed date
		if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
			return new Date(year, month, day);
		}

		return null; // Return null if the date could not be parsed
	}

	public is12Hours(): boolean {
		return this._config.timeFormat.includes('hh');
	}

	public getDateDisplayInterface(datetime: Date | null): DateTimeDisplayInterface {
		const dateFormat = this.getDateFormat();
		const timeFormat = this.getTimeFormat();

		// Extract placeholders from dateFormat and timeFormat
		const dayPlaceholder = dateFormat.match(/d+/)?.[0] || 'dd';
		const monthPlaceholder = dateFormat.match(/M+/)?.[0] || 'mm';
		const yearPlaceholder = dateFormat.match(/y+/)?.[0] || 'yyyy';
		const hoursPlaceholder = timeFormat ? (timeFormat.match(/[Hh]+/)?.[0] || 'hh') : 'hh';
		const minutesPlaceholder = timeFormat ? (timeFormat.match(/m+/)?.[0] || 'mm') : 'mm';
		const secondsPlaceholder = timeFormat ? (timeFormat.match(/s+/)?.[0] || 'ss') : 'ss';

		if (datetime === null) {
			return {
				day: dayPlaceholder,
				month: monthPlaceholder,
				year: yearPlaceholder,
				hours: hoursPlaceholder,
				minutes: minutesPlaceholder,
				seconds: secondsPlaceholder,
				amPm: '--',
				gmtOffset: '--'
			};
		}

		const hours = this.is12Hours() ? (datetime.getHours() % 12 || 12) : datetime.getHours();

		return {
			day: this.padZero(datetime.getDate()),
			month: this.padZero(datetime.getMonth() + 1),
			year: datetime.getFullYear().toString(),
			hours: this.padZero(hours),
			minutes: this.padZero(datetime.getMinutes()),
			seconds: this.padZero(datetime.getSeconds()),
			amPm: this.getAmPm(datetime.getHours()),
			gmtOffset: this.formatTimezone(datetime)
		};
	}

	public setSelectedDate(date: Date | null): void {
		if (date === null) {
			this._selectedDate = null;
			return;
		}

		if (isNaN(date.getTime())) {
			console.warn('Invalid date provided:', date);
			return;
		}

		if (!this._selectedDate) {
			this._selectedDate = new Date(date.getTime());
		} else {
			this._selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
		}

		if (this._selectedDateRange?.startDate) {
			const { startDate: startDate } = this._selectedDateRange;
			this._selectedDate.setHours(startDate.getHours(), startDate.getMinutes(), startDate.getSeconds(), startDate.getMilliseconds());
		}
	}

	public setSelectedDateRange(startDate: Date | null = null, endDate: Date | null = null): void {
		this._selectedDateRange = { startDate, endDate };
	}

	public setSelectedDateRangeStart(date: Date) {
		this._selectedDateRange.startDate = date;
	}

	public setSelectedDateRangeEnd(date: Date) {
		this._selectedDateRange.endDate = date;
	}

	public getSelectedDateInterface(): SelectedDateInterface {
		return { selectedDate: this._selectedDate, selectedDateRange: this._selectedDateRange };
	}

	public getSelectedDate(): Date | null {
		return this._selectedDate;
	}

	public getSelectedDateRange(): DateRangeInterface {
		return this._selectedDateRange;
	}

	public set minDate(date: Date | null) {
		if (date) {
			this._config.minDate = date;
		} else {
			delete this._config.minDate;
		}
	}

	public set maxDate(date: Date | null) {
		if (date) {
			this._config.maxDate = date;
		} else {
			delete this._config.maxDate;
		}
	}
};

/**
 * Adds a custom locale to the `DefaultConfig.locales` object.
 *
 * @param {string} locale - The locale identifier.
 * @param {LocaleConfigInterface} config - The configuration object for the locale.
 */
export const addLocale = (locale: string, config: LocaleConfigInterface) => {
	DefaultConfig.locales = { ...DefaultConfig.locales, [locale]: config };
};
